import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable, of, forkJoin } from "rxjs";
import { catchError, delay, map, switchMap, take } from "rxjs/operators";
import { of as observableOf, BehaviorSubject, Subject } from "rxjs";
import { environment } from "../environments/environment";
import { StorageService, StorageKeys } from "./storage.service";
import { WindowService } from "./window.service";
import { LogService } from "./log.service";
import moment from "moment";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";
import { format } from "date-fns";
import { Order, OrderLineItem, OrderFulfillment, OrderImage, OrderDiscountLineItem, OrderPaymentLineItem } from "../models";

// TypeScript interfaces for Fender Shopify orders
export interface FenderShopifyTaxLine {
  rate: number;
  title: string;
  priceSet: {
    amount: string;
    currency: string;
  };
}

export interface FenderShopifyLineItem {
  id: string;
  title: string;
  quantity: number;
  image: {
    url: string;
    src: string;
  };
  name: string;
  sku: string;
  variantTitle: string;
  originalTotal: {
    amount: string;
    currency: string;
  };
  discountedTotal: {
    amount: string;
    currency: string;
  };
  taxLines: FenderShopifyTaxLine[];
  /** True when the line item has been refunded (detected by matching refund delta against item total+tax). */
  refunded?: boolean;
  /** Dollar amount refunded for this line item (discountedTotal + allocated tax). */
  refundedAmount?: number;
}

export interface FenderShopifyTrackingInfo {
  company: string;
  number: string;
  url: string;
}

export interface FenderShopifyFulfillment {
  status: string;
  name: string;
  createdAt: string;
  deliveredAt: string | null;
  displayStatus: string;
  estimatedDeliveryAt: string | null;
  inTransitAt: string | null;
  updatedAt: string;
  trackingInfo: FenderShopifyTrackingInfo[];
}

export interface FenderShopifyOrder {
  id: string;
  name: string;
  note: string | null;
  email: string;
  phone: string | null;
  paymentGatewayNames: string[];
  processedAt: string;
  tags: string[];
  fullyPaid: boolean;
  createdAt: string;
  totalPrice: {
    amount: string;
    currency: string;
  };
  fulfillable: boolean;
  fulfillments: FenderShopifyFulfillment[];
  lineItems: FenderShopifyLineItem[];
  description: string;
  billingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    address2?: string | null;
    city?: string;
    province?: string;
    country?: string;
    zip?: string;
    phone?: string | null;
  };
  shippingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    address2?: string | null;
    city?: string;
    province?: string;
    country?: string;
    zip?: string;
    phone?: string | null;
  };
  currentShippingPriceSet?: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  currentTotalDiscountsSet?: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  /** Post-refund total (differs from totalPrice when refunds exist). */
  currentTotalPrice?: {
    amount: string;
    currency: string;
  };
  /** Total amount refunded across all refund transactions (shop money). */
  refundedAmount?: number;
  discountCode?: string | null;
  discountCodes?: string[];
  /** Discount applications (percentage or amount off line items / shipping). */
  discountApplications?: {
    edges: Array<{
      node: {
        index: number;
        targetSelection: string;
        targetType: "LINE_ITEM" | "SHIPPING_LINE";
        value: { percentage?: number; amount?: string; currencyCode?: string };
      };
    }>;
  };
  /** Payment transactions (e.g. gift card, shopify_payments). */
  transactions?: Array<{
    id: string;
    gateway: string;
    formattedGateway: string;
    amountSet?: { shopMoney: { amount: string; currencyCode: string } };
    receiptJson?: string;
  }>;
}

export interface FenderUserShopAddress {
  id: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  zip: string;
  phone: string;
  name: string;
}

export interface FenderUserShopData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  multipassIdentifier: string;
  addresses: FenderUserShopAddress[];
  createdAt: string;
  updatedAt: string;
  numberOfOrders: string;
  state: string;
  amountSpent: {
    amount: string;
    currencyCode: string;
  };
  verifiedEmail: boolean;
  taxExempt: boolean;
  tags: string[];
  defaultAddress: FenderUserShopAddress;
  storeId: string;
  storeUrl: string;
  ssoStoreUrl: string;
  ssoStoreId: string;
  shopifyId: string;
}

export interface FenderUserShopAddress {
  id: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  zip: string;
  phone: string;
  name: string;
}

export interface FenderUserShopData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  multipassIdentifier: string;
  addresses: FenderUserShopAddress[];
  createdAt: string;
  updatedAt: string;
  numberOfOrders: string;
  state: string;
  amountSpent: {
    amount: string;
    currencyCode: string;
  };
  verifiedEmail: boolean;
  taxExempt: boolean;
  tags: string[];
  defaultAddress: FenderUserShopAddress;
  storeId: string;
  storeUrl: string;
  ssoStoreUrl: string;
  ssoStoreId: string;
  shopifyId: string;
}
@Injectable({
  providedIn: "root",
})
export class OrderService {
  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private window: WindowService,
    private _log: LogService,
    private _helpers: HelperService,
    public userService: UserService
  ) {
    this._helpers.userID = this.userService.user ? this.userService.user.id : undefined;
  }

  public fenderUserShopData: FenderUserShopData;

  public shopOrderPlaced$: EventEmitter<string> = new EventEmitter();

  /**
   * The Fender orders API validates the request Origin. Browsers send it
   * automatically (and forbid setting it manually), but the NativeScript
   * runtime sends none — which the API rejects — so provide one there.
   */
  private _fenderOrdersHttpOptions() {
    const args: any = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    if (typeof document === "undefined") {
      // Lowercase on purpose. The admin-actions API reads this header case-sensitively, as
      // `origin`: HTTP/2 lowercases every header name, so iOS (and browsers, which set Origin
      // themselves) always matched, but Android's HttpURLConnection speaks HTTP/1.1 and sends the
      // name as written — "Origin" came back 400 "The request origin is invalid", the Shopify
      // account lookup failed, and Android showed no orders at all (2026-10-20). Header names are
      // case-insensitive in HTTP, so lowercase is correct everywhere.
      args["origin"] = environment.fenderUrl.replace(/\/$/, "");
    }
    return { headers: new HttpHeaders(args) };
  }

  private _orders: any;
  get orders() {
    if (!this._orders) this._orders = this.storage.getItem(StorageKeys.ORDERS);
    return this._orders;
  }

  set orders(orders) {
    this._orders = orders;
    this.storage.setItem(StorageKeys.ORDERS, orders);
  }

  public orders$: Subject<any> = new Subject();
  public recentOrders$: Subject<any> = new Subject();
  getOrders(fresh?, recent?) {
    let cachedOrders = this.storage.getItem(StorageKeys.ORDERS);
    if (!fresh && this.storage.checkTimestamp("orders") && cachedOrders) {
      return observableOf(cachedOrders);
    } else {
      return this.http.get(environment.paeApiUrl + `user/orders`, this._helpers.getHttpOptions()).pipe(
        map((result: any) => {
          this.storage.setTimestamp("orders", 1);
          this.storage.setItem(StorageKeys.ORDERS, result);
          return result;
        })
      );
    }
  }

  /** getOrder() Returns a cached order detail. */
  getOrder(id) {
    var order = this.orders.filter((o) => o.id == id)[0];
    return order;
  }

  getFenderShopifyAccountDetails(email, force?, siteID?) {
    let storedShopifyAccountDetails = this.storage.getItem(StorageKeys.FENDER_SHOPIFY_ACCOUNT_DETAILS);
    if (!force && this.storage.checkTimestamp("fender_shopify_account_details") && storedShopifyAccountDetails) {
      this.fenderUserShopData = storedShopifyAccountDetails;
      return observableOf(this.fenderUserShopData);
    } else {
      let headers = this._fenderOrdersHttpOptions();
      if (!siteID) siteID = "PRESONUS_US";
      const getCustomerStoreData = this.http.post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "getCustomerStoreData",
          siteID: siteID,
          data: { customerQuery: { email: email } },
        },
        headers
      );
      const getCustomerStoreDataAll = this.http.post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "getCustomerStoreDataAll",
          siteID: siteID,
          data: { customerQuery: { email: email } },
        },
        headers
      );
      return forkJoin({ customerStoreData: getCustomerStoreData, customerStoreDataAll: getCustomerStoreDataAll }).pipe(
        map(({ customerStoreData, customerStoreDataAll }: { customerStoreData: any; customerStoreDataAll: any }) => {
          const result = customerStoreData;
          if (result?.data?.id) {
            this.fenderUserShopData = result?.data;
            this.fenderUserShopData.shopifyId = result.data.id.split("gid://shopify/Customer/")[1];
          }
          if (this.fenderUserShopData) {
            this.storage.setTimestamp("fender_shopify_account_details", 72);
            this.storage.setItem(StorageKeys.FENDER_SHOPIFY_ACCOUNT_DETAILS, this.fenderUserShopData);
          }
          if (customerStoreDataAll?.data != null) {
            this.storage.setTimestamp("fender_shopify_account_details_all", 72);
            this.storage.setItem(StorageKeys.FENDER_SHOPIFY_ACCOUNT_DETAILS_ALL, customerStoreDataAll.data);
          }
          return this.fenderUserShopData;
        })
      );
    }
  }

  updateFenderShopifyUser(
    email: string,
    siteID?: string,
    data?: {
      firstName?: string;
      lastName?: string;
      email?: string;
    }
  ) {
    let headers = this._fenderOrdersHttpOptions();
    if (!siteID) siteID = "PRESONUS_US";
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "updateCustomerDataWithoutShopifyIDOrStoreID",
          siteID: siteID,
          data: {
            customerQuery: {
              email: email,
            },
            customerData: data,
          },
        },
        headers
      )
      .pipe(
        delay(1000),
        switchMap((result: any) => {
          return this.getFenderShopifyAccountDetails(data.email, true, siteID);
        })
      );
  }

  /**
   * Fetches Fender Shopify orders for a single store. Used by getFenderShopifyOrders for both
   * single-store and multi-store (no recursion).
   */
  private fetchFenderShopifyOrdersForStore(shopifyId: string, storeId: string): Observable<FenderShopifyOrder[]> {
    const headers = this._fenderOrdersHttpOptions();
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "getCustomerOrders",
          siteID: storeId,
          data: { customerQuery: shopifyId },
        },
        headers
      )
      .pipe(
        map((result: any) => {
          return this.parseFenderShopifyOrders(result);
        })
      );
  }

  getFenderShopifyOrders(shopifyId: string, storeId?: string): Observable<FenderShopifyOrder[]> {
    const customers = this.storage.getItem(StorageKeys.FENDER_SHOPIFY_ACCOUNT_DETAILS_ALL);
    if (customers?.length > 1) {
      const orderObservables = customers.map((customer: any) => {
        return this.fetchFenderShopifyOrdersForStore(customer.id.split("gid://shopify/Customer/")[1], customer.storeId).pipe(catchError(() => of([] as FenderShopifyOrder[])));
      });
      return forkJoin(orderObservables).pipe(map((orderArrays: FenderShopifyOrder[][]) => orderArrays.flat()));
    }
    const effectiveStoreId = storeId ?? this.fenderUserShopData?.storeId;
    return this.fetchFenderShopifyOrdersForStore(shopifyId, effectiveStoreId);
  }

  public ordersById: any = {};
  getAllUserOrders(email: string, fresh?: boolean, siteID?: string): Observable<Order[]> {
    // A failed Shopify lookup leaves the PreSonus orders to show on their own, the same as an
    // account with no Shopify customer — it used to fail the whole chain, and the page showed
    // nothing at all.
    return this.getFenderShopifyAccountDetails(email, fresh, siteID).pipe(
      catchError(() => of(null as FenderUserShopData)),
      switchMap((accountDetails: FenderUserShopData) => {
        if (!accountDetails?.shopifyId) {
          // If no Shopify account exists, just return regular orders
          return this.getOrders(fresh).pipe(map((orders) => this.transformPresonusOrderData(orders)));
        }

        // Call both order methods in parallel
        return forkJoin({
          orders: this.getOrders(fresh).pipe(catchError(() => of([]))),
          fenderShopifyOrders: this.getFenderShopifyOrders(accountDetails.shopifyId, accountDetails.storeId).pipe(catchError(() => of([] as FenderShopifyOrder[]))),
        }).pipe(
          map((result) => {
            const presonusOrders = this.transformPresonusOrderData(result.orders);
            const fenderOrders = this.transformFenderOrderData(result.fenderShopifyOrders);
            // Combine both arrays into one
            return [...presonusOrders, ...fenderOrders];
          })
        );
      })
    );
  }

  transformPresonusOrderData(orders: any[]): Order[] {
    // Filter out recurring orders
    const filteredOrders = orders.filter((order) => order.type !== "recurring");

    return filteredOrders.map((order) => {
      const payload = order.order_ipn?.payload;

      // Check if this is a Stripe invoice event (e.g., invoice.paid)
      const isStripeEvent = order.order_ipn?.object === "event" && order.order_ipn?.data?.object;

      // Check if this is a BlueSnap/Plimus order (Plimus was BlueSnap's previous name)
      const isBlueSnapOrder =
        order.order_ipn?.order?.["order-id"] ||
        order.order_ipn?.invoiceInfoURL?.includes("bluesnap.com") ||
        order.order_ipn?.invoiceInfoURL?.includes("plimus.com") ||
        order.order_ipn?.invoiceURL?.includes("bluesnap.com") ||
        order.order_ipn?.invoiceURL?.includes("plimus.com") ||
        (order.order_ipn?.contractId && order.order_ipn?.contractName);

      if (!payload && !isStripeEvent && !isBlueSnapOrder) {
        // Fallback for orders without Shopify payload, Stripe event, or BlueSnap data
        return this.transformLegacyPresonusOrder(order);
      }

      // Handle Stripe invoice events
      if (isStripeEvent) {
        return this.transformStripeInvoiceOrder(order);
      }

      // Handle BlueSnap orders
      if (isBlueSnapOrder) {
        return this.transformBlueSnapOrder(order);
      }

      // Transform line items
      const items: OrderLineItem[] =
        payload.line_items?.map((item: any) => ({
          id: item.id,
          title: item.title,
          variantTitle: item.variant_title,
          quantity: item.quantity,
          price: parseFloat(item.price),
          total: parseFloat(item.price) * item.quantity,
          sku: item.sku,
          image: item.image ? { url: item.image } : undefined,
          taxAmount: item.tax_lines?.reduce((sum: number, tax: any) => sum + parseFloat(tax.price), 0) || 0,
          discountAmount: parseFloat(item.total_discount || 0),
        })) || [];

      // Transform fulfillments
      const fulfillments: OrderFulfillment[] =
        payload.fulfillments?.map((f: any) => ({
          id: f.id,
          name: f.name,
          status: f.status,
          displayStatus: f.shipment_status || f.status,
          createdAt: f.created_at,
          deliveredAt: f.delivered_at || null,
          trackingCompany: f.tracking_company,
          trackingNumber: f.tracking_number,
          trackingUrl: f.tracking_url,
          trackingInfo:
            f.tracking_numbers?.map((num: string, idx: number) => ({
              company: f.tracking_company,
              number: num,
              url: f.tracking_urls?.[idx],
            })) || [],
        })) || [];

      // Generate description from line items
      const description = items.map((item) => `${item.title} (${item.quantity})`).join(", ");

      const transformedOrder: Order = {
        id: order.idorder_history || order.id,
        name: payload.name,
        orderNumber: payload.order_number,
        source: "presonus",
        shopifyId: payload.id ? `gid://shopify/Order/${payload.id}` : undefined,
        date: order.order_date || payload.created_at,
        email: payload.email,

        // Financial details
        subtotal: parseFloat(payload.subtotal_price || 0),
        total: parseFloat(payload.total_price || 0),
        tax: parseFloat(payload.total_tax || 0),
        discount: parseFloat(payload.total_discounts || 0),
        shipping: parseFloat(payload.total_shipping_price_set?.shop_money?.amount || 0),
        currency: payload.currency || "USD",

        // Status
        paymentStatus: payload.financial_status || "unknown",
        fulfillmentStatus: payload.fulfillment_status || "unfulfilled",

        // Items and fulfillments
        items: items,
        fulfillments: fulfillments,

        // Additional details
        description: description,
        note: payload.note,
        tags: payload.tags?.split?.(", ") || payload.tags || [],
      };

      this.ordersById[transformedOrder.id] = transformedOrder;
      return transformedOrder;
    });
  }

  /**
   * Transform Stripe invoice event data into Order model
   * Handles orders from Stripe webhooks (invoice.paid events)
   */
  transformStripeInvoiceOrder(order: any): Order {
    const invoice = order.order_ipn.data.object;

    // Transform line items from Stripe invoice lines
    // Filter out tax lines (AvaTax) and only include actual product items
    const lineItemsData = invoice.lines?.data || [];
    const items: OrderLineItem[] = lineItemsData
      .filter((item: any) => !item.metadata?.Is_AvaTax_Line)
      .map((item: any) => ({
        id: item.id,
        title: item.description || "Item",
        variantTitle: item.price?.nickname || undefined,
        quantity: item.quantity || 1,
        // Stripe amounts are in cents, convert to dollars
        price: (item.amount || 0) / 100,
        total: (item.amount || 0) / 100,
        sku: item.price?.product || undefined,
        image: undefined,
        taxAmount: item.tax_amounts?.reduce((sum: number, tax: any) => sum + (tax.amount || 0), 0) / 100 || 0,
        discountAmount: 0,
      }));

    // Calculate tax from tax line items or from total_tax_amounts
    const taxAmount = invoice.tax ? invoice.tax / 100 : (invoice.total_tax_amounts?.reduce((sum: number, tax: any) => sum + (tax.amount || 0), 0) || 0) / 100;

    // Generate description from line items or use order details
    let description = items.map((item) => `${item.title} (${item.quantity})`).join(", ");
    if (!description && order.details?.length > 0) {
      description = order.details.map((d: any) => d.title).join(", ");
    }

    const transformedOrder: Order = {
      id: order.idorder_history || order.id,
      name: invoice.number || order.reference_number,
      orderNumber: invoice.number,
      source: "presonus",
      date: order.order_date || new Date(invoice.created * 1000).toISOString(),
      email: invoice.customer_email,

      // Financial details - Stripe amounts are in cents
      subtotal: (invoice.subtotal || 0) / 100,
      total: (invoice.total || invoice.amount_paid || 0) / 100,
      tax: taxAmount,
      discount: (invoice.total_discount_amounts?.reduce((sum: number, d: any) => sum + (d.amount || 0), 0) || 0) / 100,
      shipping: (invoice.amount_shipping || 0) / 100,
      currency: (invoice.currency || "usd").toUpperCase(),

      // Status
      paymentStatus: invoice.status === "paid" ? "paid" : invoice.status || "unknown",
      fulfillmentStatus: "fulfilled", // Stripe invoices are typically digital fulfillment

      // Items and fulfillments
      items: items,
      fulfillments: [],

      // Additional details
      description: description,
      note: invoice.description,
      tags: [],

      // Invoice URLs for receipts
      invoiceUrl: invoice.hosted_invoice_url,
      invoicePdf: invoice.invoice_pdf,
    };

    this.ordersById[transformedOrder.id] = transformedOrder;
    return transformedOrder;
  }

  /**
   * Transform BlueSnap order data into Order model
   * Handles legacy BlueSnap payment processor orders (both cart-based and contract-based formats)
   */
  transformBlueSnapOrder(order: any): Order {
    const ipn = order.order_ipn;
    const cart = ipn.order?.cart;
    const invoice = ipn.postSaleInfo?.invoices?.invoice || ipn.order?.["post-sale-info"]?.invoices?.invoice;
    const transaction = invoice?.["financial-transactions"]?.["financial-transaction"];
    const contactInfo = transaction?.["invoice-contact-info"];

    // Check if this is a contract-based order (flat structure) vs cart-based order (nested structure)
    const isContractBasedOrder = ipn.contractId && ipn.contractName && !cart;

    // Helper to decode HTML entities (BlueSnap uses HTML encoding)
    const decodeHtmlEntities = (str: string): string => {
      if (!str) return str;
      return str
        .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'");
    };

    // Helper to parse price strings that may have commas (e.g., "2,285.00")
    const parsePrice = (priceStr: string | number): number => {
      if (typeof priceStr === "number") return priceStr;
      if (!priceStr) return 0;
      // Remove commas and parse
      return parseFloat(String(priceStr).replace(/,/g, "")) || 0;
    };

    // Parse products from the JSON string if available
    let parsedProducts: any[] = [];
    if (ipn.products) {
      try {
        const productsObj = JSON.parse(decodeHtmlEntities(ipn.products));
        parsedProducts = Object.values(productsObj);
      } catch (e) {
        // Products parsing failed, will use cart items instead
      }
    }

    // Build line items from cart or parsed products
    const items: OrderLineItem[] = [];

    if (isContractBasedOrder) {
      // Contract-based order: use contractName, contractPrice, etc.
      // Use charge/local price if currency is not USD, otherwise use base price
      const isLocalCurrency = ipn.currency && ipn.currency !== "USD";
      const contractPrice = isLocalCurrency
        ? parsePrice(ipn.contractChargePrice) || parsePrice(ipn.contractLocalPrice) || parsePrice(ipn.contractPrice)
        : parsePrice(ipn.contractPrice) || parsePrice(ipn.contractLocalPrice);
      const quantity = parseInt(ipn.quantity) || 1;

      // Try to get title from order details first (usually more descriptive), combine productName + contractName for full title
      let title = order.details?.[0]?.title;
      if (!title) {
        // Combine productName and contractName if both exist (e.g., "Studio One 2" + "Artist" = "Studio One 2 Artist")
        if (ipn.productName && ipn.contractName && ipn.productName !== ipn.contractName) {
          title = `${ipn.productName} ${ipn.contractName}`;
        } else {
          title = ipn.contractName || ipn.productName;
        }
      }

      items.push({
        id: ipn.contractId,
        title: decodeHtmlEntities(title),
        quantity: quantity,
        price: contractPrice,
        total: contractPrice * quantity,
        sku: ipn.productId || ipn.contractId,
      });
    } else if (parsedProducts.length > 0) {
      // Use parsed products for better item details
      parsedProducts.forEach((product: any) => {
        items.push({
          id: product.id || product.contractId,
          title: decodeHtmlEntities(product.name),
          quantity: product.qty || 1,
          price: parsePrice(product.price),
          total: parsePrice(product.subtotal) || parsePrice(product.price),
          sku: product.contractId,
          image: product.image ? { url: decodeHtmlEntities(product.image).replace(/\{\{slash\}\}/g, "/") } : undefined,
        });
      });
    } else if (cart?.["cart-item"]) {
      // Fallback to cart items
      const cartItems = Array.isArray(cart["cart-item"]) ? cart["cart-item"] : [cart["cart-item"]];
      cartItems.forEach((item: any) => {
        items.push({
          id: item.sku?.["sku-id"],
          title: decodeHtmlEntities(item.sku?.["sku-name"] || "Item"),
          quantity: parseInt(item.quantity) || 1,
          price: parsePrice(item["item-sub-total"]),
          total: parsePrice(item["item-sub-total"]),
          sku: item.sku?.["sku-id"],
        });
      });
    }

    // If still no items, use order details
    if (items.length === 0 && order.details?.length > 0) {
      order.details.forEach((detail: any) => {
        // For fallback, try to get price from various sources
        const itemPrice = parsePrice(ipn.contractPrice) || parsePrice(ipn.invoiceAmount);
        items.push({
          id: detail.order_id,
          title: detail.title,
          quantity: 1,
          price: itemPrice,
          total: itemPrice,
          sku: detail.product_key,
        });
      });
    }

    // Get financial details
    // For contract-based orders, invoiceAmount might be 0 if fully paid by coupon
    // In that case, use contractPrice as the original subtotal
    let subtotal: number;
    let total: number;
    let discount: number;

    // Determine if we should use local currency amounts
    const isLocalCurrency = ipn.currency && ipn.currency !== "USD";

    if (isContractBasedOrder) {
      // Contract-based: original price is contractPrice, discount is couponValue
      // Use charge/local amounts for non-USD currencies
      const originalPrice = isLocalCurrency
        ? parsePrice(ipn.contractChargePrice) || parsePrice(ipn.contractLocalPrice) || parsePrice(ipn.contractPrice)
        : parsePrice(ipn.contractPrice) || parsePrice(ipn.contractLocalPrice);

      discount = isLocalCurrency ? parsePrice(ipn.couponChargeValue) || parsePrice(ipn.couponValue) || 0 : parsePrice(ipn.couponValue) || parsePrice(ipn.couponChargeValue) || 0;

      total = isLocalCurrency
        ? parsePrice(ipn.invoiceChargeAmount) || parsePrice(ipn.invoiceLocalAmount) || parsePrice(ipn.invoiceAmount) || Math.max(0, originalPrice - discount)
        : parsePrice(ipn.invoiceAmount) || parsePrice(ipn.invoiceChargeAmount) || Math.max(0, originalPrice - discount);

      subtotal = originalPrice;
    } else {
      // Cart-based order
      total = isLocalCurrency
        ? parsePrice(ipn.invoiceChargeAmount) || parsePrice(ipn.invoiceLocalAmount) || parsePrice(ipn.invoiceAmount)
        : parsePrice(ipn.invoiceAmount) || parsePrice(ipn.invoiceAmountUSD) || parsePrice(cart?.["total-cart-cost"]);
      discount = isLocalCurrency ? parsePrice(ipn.couponChargeValue) || parsePrice(ipn.couponValue) || 0 : parsePrice(ipn.couponValue) || 0;
      subtotal = total + discount; // Add back discount to get subtotal
    }

    const tax = isLocalCurrency
      ? parsePrice(ipn.taxChargeAmount) || parsePrice(ipn.taxAmount) || parsePrice(cart?.tax) || 0
      : parsePrice(ipn.taxAmount) || parsePrice(ipn.taxAmountUSD) || parsePrice(ipn.taxChargeAmount) || parsePrice(cart?.tax) || 0;

    // Generate description from line items or order details
    let description = items.map((item) => `${item.title} (${item.quantity})`).join(", ");
    if (!description && order.details?.length > 0) {
      description = order.details.map((d: any) => d.title).join(", ");
    }

    // Get email (decode HTML entities)
    const email = decodeHtmlEntities(ipn.email || ipn.invoiceEmail || contactInfo?.email);

    // Determine payment status
    let paymentStatus = "paid";
    if (transaction?.status) {
      paymentStatus = transaction.status.toLowerCase() === "approved" ? "paid" : transaction.status.toLowerCase();
    } else if (ipn.transactionType === "CHARGE" || total === 0) {
      // If it's a charge transaction or total is 0 (free/coupon), consider it paid
      paymentStatus = "paid";
    }

    const transformedOrder: Order = {
      id: order.idorder_history || order.id,
      name: ipn.referenceNumber || ipn.invoiceId || order.reference_number,
      orderNumber: ipn.referenceNumber || invoice?.["invoice-id"],
      source: "presonus",
      date: order.order_date || ipn.transactionDate,
      email: email,

      // Financial details
      subtotal: subtotal,
      total: total,
      tax: tax,
      discount: discount,
      shipping: 0,
      currency: ipn.currency || ipn.invoiceChargeCurrency || cart?.["charged-currency"] || "USD",

      // Status
      paymentStatus: paymentStatus,
      fulfillmentStatus: "fulfilled",

      // Items and fulfillments
      items: items,
      fulfillments: [],

      // Coupon code if used
      discountCodes: ipn.couponCode ? [ipn.couponCode] : order.coupon_code ? [order.coupon_code] : [],

      // Billing address from BlueSnap contact info or flat IPN fields
      billingAddress: contactInfo
        ? {
            firstName: contactInfo["first-name"],
            lastName: contactInfo["last-name"],
            address1: contactInfo.address1,
            city: contactInfo.city,
            province: contactInfo.state,
            country: contactInfo.country?.toUpperCase(),
            zip: contactInfo.zip,
            phone: contactInfo.phone,
          }
        : ipn.invoiceAddress1 || ipn.address1 || ipn.shippingAddress1
          ? {
              firstName: ipn.invoiceFirstName || ipn.firstName || ipn.shippingFirstName,
              lastName: ipn.invoiceLastName || ipn.lastName || ipn.shippingLastName,
              address1: ipn.invoiceAddress1 || ipn.address1 || ipn.shippingAddress1,
              address2: ipn.invoiceAddress2 || ipn.address2 || ipn.shippingAddress2,
              city: ipn.invoiceCity || ipn.city || ipn.shippingCity,
              province: ipn.invoiceState || ipn.state || ipn.shippingState,
              country: (ipn.invoiceCountry || ipn.country || ipn.shippingCountry)?.toUpperCase(),
              zip: ipn.invoiceZipCode || ipn.zipCode || ipn.shippingZipCode,
              phone: ipn.invoiceWorkPhone || ipn.workPhone || ipn.invoiceMobilePhone || ipn.mobilePhone,
            }
          : undefined,

      // Additional details
      description: description,
      tags: [],

      // Invoice URL
      invoiceUrl: ipn.invoiceInfoURL || invoice?.url,
    };

    this.ordersById[transformedOrder.id] = transformedOrder;
    return transformedOrder;
  }

  transformLegacyPresonusOrder(order: any): Order {
    // Handle older PreSonus orders that don't have Shopify payload
    const items = order.orderItems || order.details || [];
    const description = items.length > 0 ? items.map((item: any) => `${item.title || item.product_key || "Item"}`).join(", ") : undefined;
    let legacyOrder: Order = {
      id: order.idorder_history || order.id,
      name: order.reference_number,
      source: "presonus",
      date: order.order_date,
      email: order.email,

      subtotal: 0,
      total: order.total || 0,
      tax: 0,
      discount: 0,
      shipping: 0,
      currency: "USD",

      paymentStatus: order.paymentStatus || "paid",
      fulfillmentStatus: "fulfilled",

      items: items,
      fulfillments: [],

      description: description,
      tags: [],
    };
    this.ordersById[legacyOrder.id] = legacyOrder;
    return legacyOrder;
  }

  transformFenderOrderData(orders: any[]): Order[] {
    return orders.map((order) => {
      // Transform line items
      const items: OrderLineItem[] =
        order.lineItems?.map((item: any) => {
          const getAttr = (key: string) => item.customAttributes?.find((a: any) => a.key === key)?.value;
          const modshopSpecsRaw = getAttr("_modshop_specs");
          const modshopImage = getAttr("_thumbnailUrl") || getAttr("_custom_image");
          const imageUrl = modshopImage || item.image?.url;
          return {
            id: item.id?.replace?.("gid://shopify/LineItem/", "") || item.id,
            title: item.title,
            variantTitle: item.variantTitle,
            quantity: item.quantity,
            price: parseFloat(item.originalTotal?.amount || 0),
            total: parseFloat(item.discountedTotal?.amount || item.originalTotal?.amount || 0),
            sku: item.sku,
            image: imageUrl ? { url: imageUrl, alt: item.title } : undefined,
            taxAmount: item.taxLines?.reduce((sum: number, tax: any) => sum + parseFloat(tax.priceSet?.amount || 0), 0) || 0,
            discountAmount: parseFloat(item.originalTotal?.amount || 0) - parseFloat(item.discountedTotal?.amount || 0),
            modshopSpecs: modshopSpecsRaw ? modshopSpecsRaw.split(",") : undefined,
            modshopSpecSheet: getAttr("_modshop_spec_sheet") || undefined,
            modshopImage: modshopImage || undefined,
            refunded: item.refunded === true,
          };
        }) || [];

      // Build discount line items from discountApplications (code + percentage/amount for display)
      const discountLineItems: OrderDiscountLineItem[] = this.buildOrderDiscountLineItems(order);
      // Build payment line items (e.g. gift cards) from transactions
      const paymentLineItems: OrderPaymentLineItem[] = this.buildOrderPaymentLineItems(order);

      // Transform fulfillments
      const fulfillments: OrderFulfillment[] =
        order.fulfillments?.map((f: any) => ({
          id: f.id,
          name: f.name,
          status: f.status,
          displayStatus: f.displayStatus,
          createdAt: f.createdAt,
          deliveredAt: f.deliveredAt,
          estimatedDeliveryAt: f.estimatedDeliveryAt,
          trackingInfo: f.trackingInfo || [],
        })) || [];

      // Calculate financial details
      const subtotal = items.reduce((sum, item) => sum + item.price, 0);
      const tax = items.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
      // Use line item discounts only — currentTotalDiscountsSet includes shipping discounts
      // which are already reflected in the shipping price (shown as $0 / free), so including
      // them here would double-count.
      const discount = items.reduce((sum, item) => sum + (item.discountAmount || 0), 0);
      const originalTotal = parseFloat(order.totalPrice?.amount || 0);
      const currentTotal = order.currentTotalPrice?.amount != null ? parseFloat(order.currentTotalPrice.amount) : originalTotal;
      // Prefer post-refund total when the order has refunds so the displayed total
      // matches what the customer was ultimately charged.
      const total = currentTotal;
      const refundedAmount = Math.max(0, originalTotal - currentTotal);
      // Use shipping cost from order level
      const shipping = order.currentShippingPriceSet?.shopMoney?.amount ? parseFloat(order.currentShippingPriceSet.shopMoney.amount) : 0;

      // Use existing description or generate from line items
      const description = order.description || items.map((item) => `${item.title} (${item.quantity})`).join(", ");

      const transformedOrder: Order = {
        id: order.id?.replace?.("gid://shopify/Order/", "") || order.id,
        name: order.name,
        source: "fender",
        shopifyId: order.shopifyId || order.id,
        date: order.createdAt || order.processedAt,
        email: order.email,

        // Financial details
        subtotal: subtotal,
        total: total,
        tax: tax,
        discount: discount,
        shipping: shipping,
        currency: order.totalPrice?.currency || order.currentShippingPriceSet?.shopMoney?.currencyCode || "USD",
        refundedAmount: refundedAmount > 0 ? refundedAmount : undefined,

        // Status
        paymentStatus: order.fullyPaid ? "paid" : "pending",
        fulfillmentStatus: order.fulfillments?.length > 0 ? "fulfilled" : order.fulfillable ? "unfulfilled" : "not_required",

        // Items and fulfillments
        items: items,
        fulfillments: fulfillments,
        discountLineItems: discountLineItems.length > 0 ? discountLineItems : undefined,
        paymentLineItems: paymentLineItems.length > 0 ? paymentLineItems : undefined,

        // Addresses
        billingAddress: order.billingAddress
          ? {
              firstName: order.billingAddress.firstName,
              lastName: order.billingAddress.lastName,
              address1: order.billingAddress.address1,
              address2: order.billingAddress.address2,
              city: order.billingAddress.city,
              province: order.billingAddress.province,
              country: order.billingAddress.country,
              zip: order.billingAddress.zip,
              phone: order.billingAddress.phone,
            }
          : undefined,
        shippingAddress: order.shippingAddress
          ? {
              firstName: order.shippingAddress.firstName,
              lastName: order.shippingAddress.lastName,
              address1: order.shippingAddress.address1,
              address2: order.shippingAddress.address2,
              city: order.shippingAddress.city,
              province: order.shippingAddress.province,
              country: order.shippingAddress.country,
              zip: order.shippingAddress.zip,
              phone: order.shippingAddress.phone,
            }
          : undefined,

        // Discount codes
        discountCodes: order.discountCodes || [],

        // Additional details
        description: description,
        note: order.note,
        tags: order.tags || [],
      };

      this.ordersById[transformedOrder.id] = transformedOrder;
      return transformedOrder;
    });
  }

  /**
   * Builds display discount line items from Shopify discountApplications.
   * Handles orders with no discount code but multiple applications (e.g. automatic 10% + free shipping)
   * and orders with a discount code and one or more applications.
   * Deduplicates identical discounts (e.g. same percentage/amount applied to multiple line items).
   */
  private buildOrderDiscountLineItems(order: any): OrderDiscountLineItem[] {
    const edges = order.discountApplications?.edges;
    if (!edges?.length) {
      return [];
    }
    const codes = order.discountCodes || (order.discountCode ? [order.discountCode] : []);
    const currency = order.totalPrice?.currency || order.currentTotalDiscountsSet?.shopMoney?.currencyCode || "USD";
    const isEmployeeDiscount = order.tags?.some((tag: string) => tag.includes("EMPLOYEE-DISCOUNT-PROGRAM")) ?? false;

    const result = edges.map((edge: any, i: number) => {
      const node = edge.node || {};
      const value = node.value || {};
      const targetType = node.targetType === "SHIPPING_LINE" ? "SHIPPING_LINE" : "LINE_ITEM";
      const isPercentage = value.percentage != null;
      const valueNum = isPercentage ? value.percentage : parseFloat(value.amount || "0");
      const code = codes[i] ?? codes[0] ?? null;

      let title: string;
      let translationKey: string | undefined;
      let translationParams: any;

      if (isPercentage) {
        if (isEmployeeDiscount && targetType === "LINE_ITEM") {
          title = `${valueNum}% Employee Discount`;
          translationKey = "order_detail.employee_discount";
          translationParams = { percentage: valueNum };
        } else if (valueNum === 100 && targetType === "SHIPPING_LINE") {
          title = `Free shipping`;
          translationKey = "order_detail.free_shipping";
        } else if (targetType === "SHIPPING_LINE") {
          title = `${valueNum}% off shipping`;
          translationKey = "order_detail.percentage_off_shipping";
          translationParams = { percentage: valueNum };
        } else {
          title = `${valueNum}% off items`;
          translationKey = "order_detail.percentage_off_items";
          translationParams = { percentage: valueNum };
        }
      } else {
        // Use common symbol for USD; otherwise show currency code
        const amountLabel = currency === "USD" ? `$${valueNum.toFixed(2)}` : `${currency} ${valueNum.toFixed(2)}`;
        if (targetType === "SHIPPING_LINE") {
          title = `${amountLabel} off shipping`;
          translationKey = "order_detail.amount_off_shipping";
          translationParams = { amount: amountLabel };
        } else {
          title = `${amountLabel} off`;
          translationKey = "order_detail.amount_off";
          translationParams = { amount: amountLabel };
        }
      }

      return {
        id: `discount-${i}`,
        code: code ?? undefined,
        title,
        translationKey,
        translationParams,
        targetType,
        value: valueNum,
        valueType: isPercentage ? "percentage" : "amount",
        currency: isPercentage ? undefined : value.currencyCode || currency,
      };
    });

    // Deduplicate: group by targetType + value + valueType + code, keep first of each group
    const seen = new Map<string, OrderDiscountLineItem>();
    const deduplicated: OrderDiscountLineItem[] = [];
    result.forEach((discount, i) => {
      const key = `${discount.targetType}-${discount.value}-${discount.valueType}-${discount.code ?? "null"}`;
      if (!seen.has(key)) {
        seen.set(key, discount);
        deduplicated.push(discount);
      }
    });

    // When exactly one non-shipping discount and order has line item discounts, put the dollar amount on that line item.
    // Use line item discount sum (not currentTotalDiscountsSet which includes shipping discounts).
    const lineItemOnly = deduplicated.filter((d) => d.targetType === "LINE_ITEM");
    const lineItemDiscountTotal = (order.lineItems || []).reduce(
      (sum: number, item: any) => sum + (parseFloat(item.originalTotal?.amount || 0) - parseFloat(item.discountedTotal?.amount || item.originalTotal?.amount || 0)),
      0
    );
    if (lineItemOnly.length === 1 && lineItemDiscountTotal > 0) {
      const single = deduplicated.find((d) => d.targetType === "LINE_ITEM");
      if (single) single.amount = lineItemDiscountTotal;
    }

    return deduplicated;
  }

  /**
   * Builds payment line items (e.g. gift cards) from order transactions for display in the summary.
   * When a gift card appears multiple times (e.g. charged then credited back during a refund),
   * net the amounts by gift_card_id so the summary shows the actual amount applied.
   */
  private buildOrderPaymentLineItems(order: any): OrderPaymentLineItem[] {
    const transactions = order.transactions;
    if (!Array.isArray(transactions) || transactions.length === 0) {
      return [];
    }
    const giftCards = transactions.filter((t: any) => t.gateway === "gift_card");
    if (giftCards.length === 0) {
      return [];
    }
    const currency = order.totalPrice?.currency || order.currentTotalDiscountsSet?.shopMoney?.currencyCode || "USD";

    // Aggregate by gift_card_id. Shopify's order transactions don't expose `kind`, so we
    // can't tell sales apart from refunds directly. Instead we pair each repeated gift_card
    // transaction as a refund of the previous one: the first occurrence is a charge, and any
    // subsequent occurrence with the same gift_card_id is treated as a credit back.
    type Agg = { id: string; amount: number; currency?: string; lastFourDigits?: string; title: string };
    const byCard = new Map<string, Agg>();
    const seenCount = new Map<string, number>();

    giftCards.forEach((t: any, i: number) => {
      const amount = t.amountSet?.shopMoney?.amount ? parseFloat(t.amountSet.shopMoney.amount) : 0;
      let lastFourDigits: string | undefined;
      let giftCardId: string | undefined;
      try {
        if (t.receiptJson) {
          const receipt = typeof t.receiptJson === "string" ? JSON.parse(t.receiptJson) : t.receiptJson;
          if (receipt.gift_card_last_characters) lastFourDigits = String(receipt.gift_card_last_characters);
          if (receipt.gift_card_id != null) giftCardId = String(receipt.gift_card_id);
        }
      } catch {
        // ignore parse errors
      }
      const key = giftCardId || `${lastFourDigits || "card"}-${i}`;
      const count = seenCount.get(key) || 0;
      seenCount.set(key, count + 1);
      // First occurrence is the charge, subsequent occurrences are refunds.
      const signed = count === 0 ? amount : -amount;
      const existing = byCard.get(key);
      if (existing) {
        existing.amount += signed;
      } else {
        byCard.set(key, {
          id: t.id?.replace?.(/gid:\/\/shopify\/OrderTransaction\//, "") || t.id || `gift-card-${i}`,
          amount: signed,
          currency: t.amountSet?.shopMoney?.currencyCode || currency,
          lastFourDigits,
          title: t.formattedGateway || "Gift Card",
        });
      }
    });

    return (
      Array.from(byCard.values())
        // Hide gift cards that netted out to zero (fully refunded back to the card).
        .filter((g) => g.amount > 0.001)
        .map((g) => ({
          id: g.id,
          title: g.title,
          translationKey: "order_detail.gift_card",
          amount: g.amount,
          currency: g.currency,
          lastFourDigits: g.lastFourDigits,
        }))
    );
  }

  getFenderGiftCards(shopifyId: string, storeId?: string): Observable<any> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let headers = this._fenderOrdersHttpOptions();
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "getCustomerGiftCards",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
          },
        },
        headers
      )
      .pipe(
        map((result: any) => {
          return this.parseFenderShopifyGiftCards(result);
        })
      );
  }

  addUpdateShopifyAddress(
    shopifyId: any,
    data: {
      address1: string;
      address2: string;
      city: string;
      company: string;
      countryCode: string;
      provinceCode: string;
      firstName: string;
      lastName: string;
      phone: string;
      zip: string;
    },
    addressId?: any,
    storeId?: any
  ): Observable<any> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let headers = this._fenderOrdersHttpOptions();
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "createUpdateDeleteCustomerAddress",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
            customerAddressFunction: addressId ? "update" : "create",
            ...(addressId && { addressId }),
            addressData: data,
          },
        },
        headers
      )
      .pipe(
        delay(1000),
        switchMap((result: any) => {
          return this.getFenderShopifyAccountDetails(this.userService.user.email, true, storeId);
        })
      );
  }

  deleteShopifyAddress(shopifyId: any, addressId: any, storeId?: any): Observable<any> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let headers = this._fenderOrdersHttpOptions();
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "createUpdateDeleteCustomerAddress",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
            customerAddressFunction: "delete",
            addressId: addressId,
          },
        },
        headers
      )
      .pipe(
        delay(1000),
        switchMap((result: any) => {
          return this.getFenderShopifyAccountDetails(this.userService.user.email, true, storeId);
        })
      );
  }

  parseFenderShopifyGiftCards(rawData: any): any[] {
    if (!rawData?.data?.getCustomerGiftCardsResponse) {
      return [];
    }
    return rawData.data.getCustomerGiftCardsResponse.map((giftCardWrapper: any) => {
      let giftCard: any = {};
      giftCard.id = giftCardWrapper.node.id;
      giftCard.balance = parseFloat(giftCardWrapper.node.balance.amount).toFixed(2) + " " + giftCardWrapper.node.balance.currencyCode;
      giftCard.code = giftCardWrapper.node.maskedCode;
      giftCard.expires = giftCardWrapper.node.expiresOn ? format(giftCardWrapper.node.expiresOn, "MM/dd/yyyy") : "Never";
      return giftCard;
    });
  }

  /**
   * Parses the raw Shopify order data into a more readable format
   * @param rawData The raw response from getFenderShopifyOrders
   * @returns Parsed order data with simplified structure
   */
  parseFenderShopifyOrders(rawData: any): FenderShopifyOrder[] {
    if (!rawData?.data?.getCustomerOrdersResponse) {
      return [];
    }

    // Prefer presentmentMoney (customer's local currency) over shopMoney (shop's default currency)
    const pm = (set: any) => set?.presentmentMoney || set?.shopMoney;

    return rawData.data.getCustomerOrdersResponse.map((orderWrapper: any) => {
      const order = orderWrapper.node;
      const lineItems: FenderShopifyLineItem[] = order.lineItems.edges.map((itemWrapper: any) => ({
        id: itemWrapper.node.id,
        title: itemWrapper.node.title,
        quantity: itemWrapper.node.quantity,
        image: {
          url: itemWrapper.node.image?.url,
          src: itemWrapper.node.image?.src,
        },
        name: itemWrapper.node.name,
        sku: itemWrapper.node.sku,
        variantTitle: itemWrapper.node.variantTitle,
        originalTotal: {
          amount: pm(itemWrapper.node.originalTotalSet).amount,
          currency: pm(itemWrapper.node.originalTotalSet).currencyCode,
        },
        discountedTotal: {
          amount: pm(itemWrapper.node.discountedTotalSet).amount,
          currency: pm(itemWrapper.node.discountedTotalSet).currencyCode,
        },
        taxLines: itemWrapper.node.taxLines.map((tax: any) => ({
          rate: tax.rate,
          title: tax.title,
          priceSet: {
            amount: pm(tax.priceSet).amount,
            currency: pm(tax.priceSet).currencyCode,
          },
        })),
        customAttributes: itemWrapper.node.customAttributes,
      }));

      // Detect refunded line items by comparing totalPriceSet vs currentTotalPriceSet.
      // Shopify's order payload doesn't tell us which line items were refunded, so we
      // infer it by matching the refund delta against each line item's (discountedTotal + taxes),
      // allowing tolerance for prorated discount allocation.
      const orderTotal = parseFloat(pm(order.totalPriceSet)?.amount || "0");
      const orderCurrentTotal = parseFloat(pm(order.currentTotalPriceSet)?.amount || String(orderTotal));
      const refundDelta = Math.max(0, orderTotal - orderCurrentTotal);
      if (refundDelta > 0.01 && lineItems.length) {
        const itemTotals = lineItems.map((li) => {
          const lineTotal = parseFloat(li.discountedTotal?.amount || "0");
          const taxTotal = (li.taxLines || []).reduce((sum, t) => sum + parseFloat(t.priceSet?.amount || "0"), 0);
          return lineTotal + taxTotal;
        });
        // Tolerance: prorated discounts can shift allocated amounts by a few dollars. Use the
        // larger of $20 or 2% of the refund delta so small orders and large orders both match.
        const tolerance = Math.max(20, refundDelta * 0.02);
        // Try to find a single item matching the refund first (most common case).
        let matchedIndex = -1;
        let bestDiff = Infinity;
        itemTotals.forEach((total, i) => {
          const diff = Math.abs(total - refundDelta);
          if (diff < bestDiff && diff <= tolerance) {
            bestDiff = diff;
            matchedIndex = i;
          }
        });
        if (matchedIndex >= 0) {
          lineItems[matchedIndex].refunded = true;
          lineItems[matchedIndex].refundedAmount = itemTotals[matchedIndex];
        } else {
          // Fallback: if no single item matches, flag all items whose total fits within the delta
          // (handles multi-item refunds). Accumulate until we approach the delta.
          const indexed = itemTotals.map((total, i) => ({ total, i })).sort((a, b) => b.total - a.total);
          let remaining = refundDelta;
          for (const { total, i } of indexed) {
            if (total <= remaining + tolerance) {
              lineItems[i].refunded = true;
              lineItems[i].refundedAmount = total;
              remaining -= total;
              if (remaining <= tolerance) break;
            }
          }
        }
      }

      // Create description from product titles
      const description = lineItems.map((item) => `${item.title} (${item.quantity})`).join(", ");

      // Parse fulfillments with proper structure
      const fulfillments = order.fulfillments.map((fulfillment: any) => ({
        status: fulfillment.status,
        name: fulfillment.name,
        createdAt: fulfillment.createdAt,
        deliveredAt: fulfillment.deliveredAt,
        displayStatus: fulfillment.displayStatus,
        estimatedDeliveryAt: fulfillment.estimatedDeliveryAt,
        inTransitAt: fulfillment.inTransitAt,
        updatedAt: fulfillment.updatedAt,
        trackingInfo: fulfillment.trackingInfo.map((tracking: any) => ({
          company: tracking.company,
          number: tracking.number,
          url: tracking.url,
        })),
      }));

      const totalRefunded = lineItems.reduce((sum, li) => sum + (li.refundedAmount || 0), 0);
      return {
        id: order.id,
        name: order.name,
        note: order.note,
        email: order.email,
        phone: order.phone,
        paymentGatewayNames: order.paymentGatewayNames,
        processedAt: order.processedAt,
        tags: order.tags,
        fullyPaid: order.fullyPaid,
        createdAt: order.createdAt,
        totalPrice: {
          amount: pm(order.totalPriceSet).amount,
          currency: pm(order.totalPriceSet).currencyCode,
        },
        currentTotalPrice: order.currentTotalPriceSet
          ? {
              amount: pm(order.currentTotalPriceSet).amount,
              currency: pm(order.currentTotalPriceSet).currencyCode,
            }
          : undefined,
        refundedAmount: totalRefunded > 0 ? totalRefunded : undefined,
        fulfillable: order.fulfillable,
        fulfillments: fulfillments,
        lineItems: lineItems,
        description: description,
        billingAddress: order.billingAddress
          ? {
              firstName: order.billingAddress.firstName,
              lastName: order.billingAddress.lastName,
              address1: order.billingAddress.address1,
              address2: order.billingAddress.address2,
              city: order.billingAddress.city,
              province: order.billingAddress.province,
              country: order.billingAddress.country,
              zip: order.billingAddress.zip,
              phone: order.billingAddress.phone,
            }
          : undefined,
        shippingAddress: order.shippingAddress
          ? {
              firstName: order.shippingAddress.firstName,
              lastName: order.shippingAddress.lastName,
              address1: order.shippingAddress.address1,
              address2: order.shippingAddress.address2,
              city: order.shippingAddress.city,
              province: order.shippingAddress.province,
              country: order.shippingAddress.country,
              zip: order.shippingAddress.zip,
              phone: order.shippingAddress.phone,
            }
          : undefined,
        currentShippingPriceSet: order.currentShippingPriceSet
          ? {
              shopMoney: {
                amount: pm(order.currentShippingPriceSet).amount,
                currencyCode: pm(order.currentShippingPriceSet).currencyCode,
              },
            }
          : undefined,
        currentTotalDiscountsSet: order.currentTotalDiscountsSet
          ? {
              shopMoney: {
                amount: pm(order.currentTotalDiscountsSet).amount,
                currencyCode: pm(order.currentTotalDiscountsSet).currencyCode,
              },
            }
          : undefined,
        discountCode: order.discountCode ?? undefined,
        discountCodes: order.discountCodes || [],
        discountApplications: order.discountApplications
          ? {
              edges: order.discountApplications.edges.map((edge: any) => ({
                node: {
                  index: edge.node?.index ?? 0,
                  targetSelection: edge.node?.targetSelection ?? "ALL",
                  targetType: edge.node?.targetType ?? "LINE_ITEM",
                  value: edge.node?.value ?? {},
                },
              })),
            }
          : undefined,
        transactions: order.transactions?.map((t: any) => ({
          id: t.id,
          gateway: t.gateway,
          formattedGateway: t.formattedGateway,
          amountSet: t.amountSet ? { shopMoney: { amount: pm(t.amountSet).amount, currencyCode: pm(t.amountSet).currencyCode } } : undefined,
          receiptJson: t.receiptJson,
        })),
      };
    });
  }

  clearCache() {
    // this.orders$.next(false);
    // this.recentOrders$.next(false);
    this._orders = false;
    this.storage.removeItem(StorageKeys.ORDERS);
  }
}
